import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import {
  toPatientPanelClaimSummary,
  toPatientPanelOverrideSummary,
  useActivePanelOverrides,
  usePatientClaimHistory,
  usePatientPanelBalances,
  type PanelBalanceStatus,
  type PanelClaimStatus,
  type PatientPanelClaim,
  type PatientPanelClaimSummary,
  type PatientPanelOverride,
  type PatientPanelOverrideSummary,
} from './api';
import styles from './PanelHistory.module.css';

const toneForBalance = (status: PanelBalanceStatus) =>
  status === 'exceeded'
    ? ('danger' as const)
    : status === 'warning'
      ? ('warning' as const)
      : ('success' as const);

const toneForClaim = (status: PanelClaimStatus) => {
  if (status === 'rejected' || status === 'cancelled') return 'danger' as const;
  if (status === 'queried') return 'warning' as const;
  if (status === 'approved' || status === 'paid') return 'success' as const;
  return 'info' as const;
};

export function PanelAndClaims({ patientId }: { patientId: string }) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const balances = usePatientPanelBalances(patientId);
  const claims = usePatientClaimHistory(patientId);
  const overrides = useActivePanelOverrides(patientId);
  const canViewClaimDetails =
    hasPermission('claims:read') || hasPermission('approvals:decide');
  const summaryClaims = claims.data?.map(toPatientPanelClaimSummary);
  const summaryOverrides = overrides.data?.map(toPatientPanelOverrideSummary);

  const overrideSummaryColumns: Column<PatientPanelOverrideSummary>[] = [
    {
      key: 'panel',
      header: t('panelHistory.panel'),
      render: (override) => <strong>{override.panelName}</strong>,
    },
    {
      key: 'type',
      header: t('panelHistory.override'),
      render: (override) => (
        <div>
          <strong>{t(`panelHistory.overrideTypes.${override.type}`)}</strong>
          <span className={styles.muted}>{override.reference}</span>
        </div>
      ),
    },
    {
      key: 'validity',
      header: t('panelHistory.validity'),
      render: (override) =>
        override.expiresAt
          ? t('panelHistory.until', {
              date: new Date(override.expiresAt).toLocaleDateString('en-GB'),
            })
          : t('panelHistory.noExpiry'),
    },
  ];
  const overrideDetailColumns: Column<PatientPanelOverride>[] = [
    overrideSummaryColumns[0],
    overrideSummaryColumns[1],
    {
      key: 'amount',
      header: t('panelHistory.approvedCoverage'),
      render: (override) => formatRM(override.approvedAmountSen),
    },
    overrideSummaryColumns[2],
    {
      key: 'recorded',
      header: t('panelHistory.recordedBy'),
      render: (override) => (
        <div>
          <strong>{override.recordedByName}</strong>
          <span className={styles.muted}>{override.reason}</span>
        </div>
      ),
    },
  ];
  const claimSummaryColumns: Column<PatientPanelClaimSummary>[] = [
    {
      key: 'claim',
      header: t('panelHistory.claim'),
      render: (claim) => (
        <div>
          <strong className="mono">{claim.claimReference}</strong>
          <span className={styles.muted}>{claim.visitDate}</span>
        </div>
      ),
    },
    {
      key: 'panel',
      header: t('panelHistory.panel'),
      render: (claim) => (
        <div>
          <strong>{claim.panelName}</strong>
          <span className={styles.muted}>{claim.branchName}</span>
        </div>
      ),
    },
    {
      key: 'claimed',
      header: t('panelHistory.claimed'),
      render: (claim) => formatRM(claim.claimedAmountSen),
    },
    {
      key: 'patient',
      header: t('panelHistory.patientShare'),
      render: (claim) => formatRM(claim.patientAmountSen),
    },
    {
      key: 'status',
      header: t('panelHistory.status'),
      render: (claim) => (
        <Badge tone={toneForClaim(claim.status)}>
          {t(`panelHistory.claimStatus.${claim.status}`)}
        </Badge>
      ),
    },
  ];
  const claimDetailColumns: Column<PatientPanelClaim>[] = [
    claimSummaryColumns[0],
    claimSummaryColumns[1],
    claimSummaryColumns[2],
    {
      key: 'approved',
      header: t('panelHistory.approved'),
      render: (claim) =>
        claim.approvedAmountSen === null
          ? '—'
          : formatRM(claim.approvedAmountSen),
    },
    claimSummaryColumns[3],
    claimSummaryColumns[4],
  ];

  const noOverrides = (
    <EmptyState
      title={t('panelHistory.noOverrides')}
      body={t('panelHistory.noOverridesBody')}
    />
  );
  const noClaims = (
    <EmptyState
      title={t('panelHistory.noClaims')}
      body={t('panelHistory.noClaimsBody')}
    />
  );

  return (
    <div className={styles.stack}>
      <section aria-labelledby="panel-balances-title">
        <h3 id="panel-balances-title" className={styles.sectionTitle}>
          {t('panelHistory.remainingLimits')}
        </h3>
        {balances.isLoading ? (
          <LoadingState />
        ) : balances.error ? (
          <ErrorState
            error={balances.error}
            onRetry={() => void balances.refetch()}
          />
        ) : balances.data?.length ? (
          <div className={styles.balanceGrid}>
            {balances.data.map((balance) => (
              <article key={balance.panelId} className={styles.balanceCard}>
                <div className={styles.balanceHeading}>
                  <strong>{balance.panelName}</strong>
                  <Badge
                    tone={toneForBalance(balance.status)}
                    icon={
                      balance.status !== 'within_limit' ? (
                        <AlertTriangle size={13} />
                      ) : undefined
                    }
                  >
                    {t(`panelHistory.balanceStatus.${balance.status}`)}
                  </Badge>
                </div>
                <p className={styles.balanceAmount}>
                  {balance.remainingSen === null
                    ? t('panelHistory.noCap')
                    : formatRM(balance.remainingSen)}
                </p>
                <p className={styles.muted}>
                  {balance.limitSen === null
                    ? t('panelHistory.usedNoCap', {
                        used: formatRM(balance.usedSen),
                      })
                    : t('panelHistory.usedOfLimit', {
                        used: formatRM(balance.usedSen),
                        limit: formatRM(balance.limitSen),
                        period: balance.periodLabel,
                      })}
                </p>
                {balance.status !== 'within_limit' && (
                  <p className={styles.warning}>
                    {balance.status === 'exceeded'
                      ? t('panelHistory.capExceeded')
                      : t('panelHistory.capWarning', {
                          threshold: balance.warningThresholdPercent,
                        })}
                  </p>
                )}
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            title={t('panelHistory.noBalances')}
            body={t('panelHistory.noBalancesBody')}
          />
        )}
      </section>

      <Card title={t('panelHistory.activeOverrides')} flush>
        {canViewClaimDetails ? (
          <DataTable
            caption={t('panelHistory.activeOverrides')}
            rows={overrides.data}
            rowKey={(override) => override.id}
            loading={overrides.isLoading}
            error={overrides.error}
            onRetry={() => void overrides.refetch()}
            empty={noOverrides}
            columns={overrideDetailColumns}
          />
        ) : (
          <DataTable
            caption={t('panelHistory.activeOverrides')}
            rows={summaryOverrides}
            rowKey={(override) => override.id}
            loading={overrides.isLoading}
            error={overrides.error}
            onRetry={() => void overrides.refetch()}
            empty={noOverrides}
            columns={overrideSummaryColumns}
          />
        )}
      </Card>

      <Card title={t('panelHistory.pastClaims')} flush>
        {canViewClaimDetails ? (
          <DataTable
            caption={t('panelHistory.pastClaims')}
            rows={claims.data}
            rowKey={(claim) => claim.id}
            loading={claims.isLoading}
            error={claims.error}
            onRetry={() => void claims.refetch()}
            empty={noClaims}
            columns={claimDetailColumns}
          />
        ) : (
          <DataTable
            caption={t('panelHistory.pastClaims')}
            rows={summaryClaims}
            rowKey={(claim) => claim.id}
            loading={claims.isLoading}
            error={claims.error}
            onRetry={() => void claims.refetch()}
            empty={noClaims}
            columns={claimSummaryColumns}
          />
        )}
      </Card>
    </div>
  );
}
